import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import type { TranslationTag } from '@swf/core/types/i18n';
import type {
  MarketId,
  MarketLevelCompoundMarketTemplateRowConfig
} from '@swf/generated/types/api/sports';
import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { notEmpty } from '@swf/sports/utils/notEmpty';

import useMarketDisplayLimit from '../../hooks/useMarketDisplayLimit';

import styles from './Table.scss';
import {
  TXTCLI_No,
  TXTCLI_Yes,
  TXTWCL_Away,
  TXTWCL_Home_8,
  TXTWCL_Neither,
  TXTWCL_Over,
  TXTWCL_Show_More,
  TXTWCL_Under
} from './TableRow.messages.json';

interface Props extends MarketLevelCompoundMarketTemplateRowConfig {
  marketIds: (MarketId | null)[];
}

const headerTranslationMap: Record<string, TranslationTag> = {
  ISP_I18N_AWAY: TXTWCL_Away,
  ISP_I18N_HOME: TXTWCL_Home_8,
  ISP_I18N_NO: TXTCLI_No,
  ISP_I18N_OVER: TXTWCL_Over,
  ISP_I18N_UNDER: TXTWCL_Under,
  ISP_I18N_YES: TXTCLI_Yes,
  ISP_I18N_NEITHER: TXTWCL_Neither
};

const SHOW_MORE_THRESHOLD = 6;

const TableRow: React.FC<Props> = ({ marketIds, columnTitles }) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const allMarkets = useSelector(widgetSelect.markets);
  const markets = marketIds
    .filter(notEmpty)
    .map((marketId) => allMarkets[marketId])
    .filter(notEmpty);
  const events = useSelector(widgetSelect.events);

  const [marketRowsToDisplay, handleShowMoreClick, limitRows] = useMarketDisplayLimit(
    markets,
    markets[0].marketId,
    SHOW_MORE_THRESHOLD
  );

  return (
    <>
      <table data-testid="compound-table-row" className={classNames(styles.table, styles.tableRow)}>
        <thead>
          <tr>
            <td />
            {columnTitles.map((title) => (
              <th scope="col" key={title}>
                <Translation {...headerTranslationMap[title]} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {marketRowsToDisplay.map(
            ({
              marketId,
              eventId,
              legTypes,
              marketName,
              marketType,
              marketStatus,
              runners,
              eventTypeId,
              competitionId
            }) => (
              <tr key={marketName}>
                <th scope="row">{marketName}</th>

                {runners.map((runner) => (
                  <td key={`${runner.selectionId}`}>
                    <Selection
                      competitionId={competitionId}
                      eventId={eventId}
                      eventTypeId={eventTypeId}
                      handicap={runner.handicap}
                      legTypes={legTypes}
                      marketId={marketId}
                      marketStatus={marketStatus}
                      marketType={marketType}
                      odds={runner.winRunnerOdds}
                      runnerResultType={runner.resultType}
                      runnerStatus={runner.runnerStatus}
                      selectionId={runner.selectionId}
                      startTime={events[eventId].eventStartTime}
                    />
                  </td>
                ))}
              </tr>
            )
          )}
        </tbody>
      </table>
      {limitRows && (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.showMore} onClick={handleShowMoreClick}>
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </>
  );
};

export default TableRow;
